import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { fmtMoney, fmtPct, fmtDate, initials, daysUntil } from "@/lib/format";
import { ArrowLeft, Trash2, Plus, Check, RotateCcw, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import ProgressBar from "@/components/ProgressBar";

export default function ParticipantDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addActivity, setAddActivity] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await endpoints.participant(id));
    } catch {
      toast.error("Failed to load participant");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const markCommitment = async (cid, isLate = false) => {
    const p = data.participant;
    const c = data.commitments.find((x) => x.id === cid);
    const sched = new Date(c.scheduled_date);
    const activityDate = new Date(sched);
    if (isLate) activityDate.setDate(activityDate.getDate() + 5);
    try {
      await endpoints.markCommitment(cid, { activity_date: activityDate.toISOString().slice(0, 10) });
      toast.success("Commitment recorded");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const resetCommitment = async (cid) => {
    try {
      await endpoints.resetCommitment(cid);
      toast.success("Reset");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const deleteActivity = async (aid) => {
    try {
      await endpoints.deleteActivity(aid);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const resolveActivity = async (aid, resolution) => {
    try {
      await endpoints.resolveActivity(aid, resolution);
      toast.success(resolution === "keep_unresolved" ? "Kept unresolved" : "Item classified");
      load();
    } catch {
      toast.error("Failed to resolve");
    }
  };

  if (loading || !data) {
    return (
      <div className="container-hi py-10 text-[13px]" style={{ color: "var(--cream-muted)" }}>
        Loading participant…
      </div>
    );
  }

  const p = data.participant;
  const trip = daysUntil(p.trip_date);
  const goalPct = p.savings_goal ? p.travel_goal_progress / p.savings_goal : 0;

  const statusChip = (s) => {
    if (s === "on_time") return <span className="chip success" data-testid="chip-on-time">On time</span>;
    if (s === "late_recovered") return <span className="chip warn" data-testid="chip-late">Late / recovered</span>;
    if (s === "missed") return <span className="chip danger" data-testid="chip-missed">Missed</span>;
    if (s === "awaiting") return <span className="chip warn" data-testid="chip-awaiting">Awaiting</span>;
    return <span className="chip" data-testid="chip-upcoming">Upcoming</span>;
  };

  const activityBadge = (t) => {
    if (t === "contribution") return <span className="chip gold">Contribution</span>;
    if (t === "trip_expense") return <span className="chip success">Trip expense</span>;
    if (t === "non_trip_leakage") return <span className="chip danger">Non-trip leakage</span>;
    return <span className="chip warn">Ambiguous</span>;
  };

  return (
    <div className="container-hi py-10" data-testid="participant-detail-page">
      <Link to="/participants" className="text-[12px] inline-flex items-center gap-1 mb-6" style={{ color: "var(--cream-dim)" }}>
        <ArrowLeft className="w-3.5 h-3.5" /> Back to roster
      </Link>

      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-5">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center text-[18px] font-mono-num"
            style={{ background: "var(--surface-3)", color: "var(--gold-light)", border: "1px solid var(--hairline-strong)" }}
          >
            {initials(p.name)}
          </div>
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase font-mono-num" style={{ color: "var(--gold)" }}>
              Participant · {p.status}
            </div>
            <h1 className="font-display text-[38px] leading-[1.05] mt-1" style={{ color: "var(--cream)" }}>
              {p.name}
            </h1>
            <div className="text-[13px] mt-2" style={{ color: "var(--cream-dim)" }}>
              {p.email} · {p.city || "—"}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {p.evaluable ? (
                <span className="chip gold">Behaviorally evaluable</span>
              ) : (
                <span className="chip">Not yet evaluable · needs ≥28d active OR ≥4 commitments due</span>
              )}
              {p.behind_flag && (
                <span className="chip danger">
                  <AlertTriangle className="w-3 h-3" /> Flagged behind
                </span>
              )}
              {p.ambiguous_pending > 0 && (
                <span className="chip warn">{fmtMoney(p.ambiguous_pending)} pending review</span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>
            Trip destination
          </div>
          <div className="font-display text-[22px] mt-1" style={{ color: "var(--cream)" }}>
            {p.trip_destination}
          </div>
          <div className="text-[12px] font-mono-num mt-1" style={{ color: "var(--gold-light)" }}>
            {fmtDate(p.trip_date)}
            {trip !== null && trip >= 0 && <span style={{ color: "var(--cream-muted)" }}> · {trip}d out</span>}
          </div>
        </div>
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <Kpi label="Adherence" value={fmtPct(p.adherence_rate)} sub={`${p.commitments_on_time}/${p.commitments_due} on time`} tone="gold" />
        <Kpi label="Preservation" value={p.evaluable ? fmtPct(p.preservation_rate) : "—"} sub={`${fmtMoney(p.preserved_savings)} preserved`} />
        <Kpi label="Contributions" value={fmtMoney(p.confirmed_contributions)} sub={`+ ${fmtMoney(p.starting_designated_amount)} starting`} />
        <Kpi label="Non-Trip Leakage" value={fmtMoney(p.non_trip_leakage)} sub={p.non_trip_leakage > 0 ? "Reduces preservation" : "None recorded"} tone={p.non_trip_leakage > 0 ? "danger" : "default"} />
      </section>

      {/* Goal progress */}
      <section className="surface mt-6 p-6" data-testid="goal-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>
              Travel Goal Progress
            </div>
            <div className="font-display text-[24px] mt-1" style={{ color: "var(--cream)" }}>
              {fmtMoney(p.travel_goal_progress)} <span style={{ color: "var(--cream-muted)" }}>/ {fmtMoney(p.savings_goal)}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono-num text-[24px]" style={{ color: "var(--gold-light)" }}>
              {fmtPct(goalPct)}
            </div>
            {p.amount_above_target > 0 && (
              <div className="text-[11px]" style={{ color: "var(--cream-muted)" }}>
                {fmtMoney(p.amount_above_target)} above target
              </div>
            )}
          </div>
        </div>
        <ProgressBar value={p.travel_goal_progress} max={p.savings_goal} tone={goalPct >= 0.9 ? "success" : "gold"} />
      </section>

      {/* Review queue for unresolved / ambiguous activity */}
      {(() => {
        const unresolved = (data.activities || []).filter((a) => !a.resolved || a.type === "ambiguous");
        if (unresolved.length === 0) return null;
        const cleanDesc = (s) => (s || "")
          .replace(/\s*[—-]\s*needs participant confirmation/i, "")
          .replace(/\s+awaiting review$/i, "")
          .trim();
        const options = [
          { key: "contribution", label: "Confirmed Contribution" },
          { key: "trip_expense", label: "Legitimate Trip Expense" },
          { key: "non_trip_leakage", label: "Non-Trip Leakage" },
          { key: "keep_unresolved", label: "Remain Unresolved" },
        ];
        return (
          <section className="mt-8" data-testid="review-queue-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display text-[22px]" style={{ color: "var(--cream)" }}>
                  Review queue
                </h2>
                <div className="text-[12px] mt-1" style={{ color: "var(--cream-dim)" }}>
                  Unresolved items are not counted toward Preserved Savings, Travel Goal Progress, or Preservation Rate until classified.
                </div>
              </div>
              <span className="chip warn">{unresolved.length} awaiting classification</span>
            </div>
            <div className="surface overflow-hidden">
              {unresolved.map((a) => (
                <div
                  key={a.id}
                  className="px-6 py-4 border-b"
                  style={{ borderColor: "var(--hairline)" }}
                  data-testid={`review-item-${a.id}`}
                >
                  <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-[13px] flex items-center gap-2" style={{ color: "var(--cream)" }}>
                        <span className="font-mono-num">{fmtMoney(a.amount)}</span>
                        <span style={{ color: "var(--cream-muted)" }}>·</span>
                        <span className="font-mono-num text-[12px]" style={{ color: "var(--cream-muted)" }}>
                          {fmtDate(a.date)}
                        </span>
                      </div>
                      <div className="text-[12px] mt-1" style={{ color: "var(--cream-dim)" }}>
                        {cleanDesc(a.description) || "Uncategorized activity awaiting admin review."}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {options.map((opt) => (
                        <button
                          key={opt.key}
                          className={opt.key === "keep_unresolved" ? "btn-ghost !py-1.5 !px-3 text-[11px]" : "btn-gold !py-1.5 !px-3 text-[11px]"}
                          onClick={() => resolveActivity(a.id, opt.key)}
                          data-testid={`resolve-${opt.key}-${a.id}`}
                          style={opt.key !== "keep_unresolved" ? { background: "var(--surface-2)", color: "var(--gold-light)", border: "1px solid rgba(201,168,76,0.35)" } : {}}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Commitments */}
      <section className="mt-8" data-testid="commitments-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-[22px]" style={{ color: "var(--cream)" }}>
            Scheduled commitments
          </h2>
          <div className="text-[11px] font-mono-num" style={{ color: "var(--cream-muted)" }}>
            {data.commitments.length} total · ±3-day on-time window
          </div>
        </div>
        <div className="surface overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-3 text-[10px] uppercase tracking-widest font-mono-num border-b" style={{ color: "var(--cream-muted)", borderColor: "var(--hairline)" }}>
            <div className="col-span-3">Scheduled</div>
            <div className="col-span-3">Activity date</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {data.commitments.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-12 px-6 py-3 items-center border-b text-[13px]"
              style={{ borderColor: "var(--hairline)" }}
              data-testid={`commitment-row-${c.id}`}
            >
              <div className="col-span-3 font-mono-num" style={{ color: "var(--cream)" }}>{fmtDate(c.scheduled_date)}</div>
              <div className="col-span-3 font-mono-num" style={{ color: "var(--cream-dim)" }}>{c.activity_date ? fmtDate(c.activity_date) : "—"}</div>
              <div className="col-span-2 font-mono-num" style={{ color: "var(--cream)" }}>{fmtMoney(c.amount)}</div>
              <div className="col-span-2">{statusChip(c.status)}</div>
              <div className="col-span-2 flex justify-end gap-2">
                {c.status === "on_time" || c.status === "late_recovered" ? (
                  <button className="btn-ghost !py-1 !px-2 text-[11px]" onClick={() => resetCommitment(c.id)} data-testid={`reset-${c.id}`}>
                    <RotateCcw className="w-3 h-3 inline mr-1" /> Reset
                  </button>
                ) : (
                  <>
                    <button className="btn-ghost !py-1 !px-2 text-[11px]" onClick={() => markCommitment(c.id, false)} data-testid={`mark-ontime-${c.id}`}>
                      <Check className="w-3 h-3 inline mr-1" /> On time
                    </button>
                    <button className="btn-ghost !py-1 !px-2 text-[11px]" onClick={() => markCommitment(c.id, true)} data-testid={`mark-late-${c.id}`}>
                      Late
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Activities */}
      <section className="mt-8" data-testid="activities-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-[22px]" style={{ color: "var(--cream)" }}>
            Activity log
          </h2>
          <button className="btn-gold inline-flex items-center gap-2" onClick={() => setAddActivity(true)} data-testid="add-activity-btn">
            <Plus className="w-3.5 h-3.5" /> Record activity
          </button>
        </div>
        <div className="surface overflow-hidden">
          {data.activities.length === 0 ? (
            <div className="px-6 py-10 text-center text-[13px]" style={{ color: "var(--cream-muted)" }}>
              No activity recorded yet.
            </div>
          ) : (
            data.activities.map((a) => {
              const isCommit = (a.description || "").startsWith("__commit__");
              const cleanDesc = (a.description || "")
                .replace(/\s*[—-]\s*needs participant confirmation/i, "")
                .replace(/\s+awaiting review$/i, "")
                .trim();
              const typeLabels = {
                contribution: "Confirmed Contribution",
                trip_expense: "Legitimate Trip Expense",
                non_trip_leakage: "Non-Trip Leakage",
                ambiguous: "Ambiguous",
              };
              return (
              <div
                key={a.id}
                className="grid grid-cols-12 px-6 py-3 items-center border-b text-[13px]"
                style={{ borderColor: "var(--hairline)" }}
                data-testid={`activity-row-${a.id}`}
              >
                <div className="col-span-2 font-mono-num" style={{ color: "var(--cream)" }}>{fmtDate(a.date)}</div>
                <div className="col-span-2">{activityBadge(a.type)}</div>
                <div className="col-span-2 font-mono-num" style={{ color: "var(--cream)" }}>{fmtMoney(a.amount)}</div>
                <div className="col-span-4 text-[12px]" style={{ color: "var(--cream-dim)" }}>
                  {isCommit ? (
                    "Contribution linked to scheduled commitment"
                  ) : (
                    <>
                      <div style={{ color: "var(--cream)" }}>{cleanDesc || "—"}</div>
                      {a.resolved && a.type !== "ambiguous" && !isCommit && (
                        <div className="text-[11px] mt-0.5" style={{ color: "var(--gold-light)" }} data-testid={`classification-${a.id}`}>
                          Confirmed classification: {typeLabels[a.type]}
                        </div>
                      )}
                      {!a.resolved && <span className="chip warn mt-1 inline-flex">Awaiting classification</span>}
                    </>
                  )}
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  {!isCommit && a.resolved && (
                    <button
                      className="btn-ghost !py-1 !px-2 text-[11px]"
                      onClick={() => resolveActivity(a.id, "keep_unresolved")}
                      data-testid={`change-classification-${a.id}`}
                    >
                      <RotateCcw className="w-3 h-3 inline mr-1" /> Change
                    </button>
                  )}
                  {!isCommit && (
                    <button onClick={() => deleteActivity(a.id)} data-testid={`delete-activity-${a.id}`} title="Delete activity">
                      <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--cream-muted)" }} />
                    </button>
                  )}
                </div>
              </div>
              );
            })
          )}
        </div>
      </section>

      {addActivity && (
        <ActivityModal
          participantId={p.id}
          onClose={() => setAddActivity(false)}
          onSaved={() => {
            setAddActivity(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone = "default" }) {
  const color = tone === "gold" ? "var(--gold-light)" : tone === "danger" ? "var(--danger)" : "var(--cream)";
  return (
    <div className="surface p-5">
      <div className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>{label}</div>
      <div className="kpi mt-3" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] mt-1" style={{ color: "var(--cream-dim)" }}>{sub}</div>}
    </div>
  );
}

function ActivityModal({ participantId, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, type: "contribution", amount: 100, description: "", resolved: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await endpoints.createActivity({
        participant_id: participantId,
        date: form.date,
        type: form.type,
        amount: Number(form.amount),
        description: form.description,
        resolved: form.type === "ambiguous" ? false : true,
      });
      toast.success("Activity recorded");
      onSaved();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(10,10,12,0.7)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="activity-modal"
    >
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="surface w-full max-w-md p-8 rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <div className="font-display text-[22px]" style={{ color: "var(--cream)" }}>Record activity</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4" style={{ color: "var(--cream-dim)" }} /></button>
        </div>
        <div className="space-y-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>Type</span>
            <select
              data-testid="field-activity-type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="px-3 py-2 rounded-md text-[13px]"
              style={{ background: "var(--surface-2)", border: "1px solid var(--hairline-strong)", color: "var(--cream)" }}
            >
              <option value="contribution">Contribution</option>
              <option value="trip_expense">Legitimate trip expense</option>
              <option value="non_trip_leakage">Non-trip leakage</option>
              <option value="ambiguous">Ambiguous / needs review</option>
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>Date</span>
            <input
              type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="field-activity-date"
              className="px-3 py-2 rounded-md text-[13px]"
              style={{ background: "var(--surface-2)", border: "1px solid var(--hairline-strong)", color: "var(--cream)" }}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>Amount (USD)</span>
            <input
              type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="field-activity-amount"
              className="px-3 py-2 rounded-md text-[13px]"
              style={{ background: "var(--surface-2)", border: "1px solid var(--hairline-strong)", color: "var(--cream)" }}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>Description</span>
            <input
              type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="field-activity-desc"
              className="px-3 py-2 rounded-md text-[13px]"
              style={{ background: "var(--surface-2)", border: "1px solid var(--hairline-strong)", color: "var(--cream)" }}
            />
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-gold" disabled={saving} data-testid="activity-modal-submit">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
